import { supabase } from '../lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Per-line-item (per-service) proposal photos. Attached to a stable `line_key`
// on the service so they survive the delete/recreate of service rows on edit.
// ─────────────────────────────────────────────────────────────────────────────

export const MAX_PHOTOS_PER_SERVICE = 10;
const BUCKET = 'user-uploads';

export interface ProposalServicePhoto {
  id: string;
  business_id: string;
  proposal_id: string;
  line_key: string;
  file_name: string | null;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  order_index: number;
  uploaded_by: string | null;
  created_at: string;
}

/** Resolve a public URL for a stored photo path. */
export function proposalServicePhotoUrl(filePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(filePath);
  return data.publicUrl;
}

/** Upload one photo for a proposal line item. Returns the saved record. */
export async function uploadProposalServicePhoto(params: {
  businessId: string;
  proposalId: string;
  lineKey: string;
  file: File;
  orderIndex?: number;
}): Promise<ProposalServicePhoto> {
  const { businessId, proposalId, lineKey, file, orderIndex = 0 } = params;
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const safeKey = lineKey.replace(/[^a-zA-Z0-9_-]/g, '');
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const filePath = `proposal-service-photos/${proposalId}/${safeKey}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, file, { cacheControl: '3600', upsert: false });
  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('proposal_service_photos')
    .insert({
      business_id: businessId,
      proposal_id: proposalId,
      line_key: lineKey,
      file_name: file.name,
      file_path: filePath,
      file_size: file.size,
      mime_type: file.type,
      order_index: orderIndex,
      uploaded_by: user?.id || null,
    })
    .select()
    .single();

  if (error) {
    // best-effort cleanup of the uploaded object
    await supabase.storage.from(BUCKET).remove([filePath]);
    throw new Error(`Failed to save photo: ${error.message}`);
  }
  return data as ProposalServicePhoto;
}

/** All per-service photos for a proposal (caller groups by line_key). */
export async function listProposalServicePhotos(proposalId: string): Promise<ProposalServicePhoto[]> {
  const { data, error } = await supabase
    .from('proposal_service_photos')
    .select('*')
    .eq('proposal_id', proposalId)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) {
    console.error('Error listing proposal service photos:', error);
    return [];
  }
  return (data || []) as ProposalServicePhoto[];
}

/** Group photos by line_key for easy per-service rendering. */
export function groupPhotosByLineKey(photos: ProposalServicePhoto[]): Record<string, ProposalServicePhoto[]> {
  const map: Record<string, ProposalServicePhoto[]> = {};
  for (const p of photos) {
    if (!p.line_key) continue;
    (map[p.line_key] ||= []).push(p);
  }
  return map;
}

/** Delete a single photo (storage + row). */
export async function deleteProposalServicePhoto(photoId: string): Promise<boolean> {
  const { data: photo } = await supabase
    .from('proposal_service_photos')
    .select('file_path')
    .eq('id', photoId)
    .single();

  if (photo?.file_path) {
    await supabase.storage.from(BUCKET).remove([photo.file_path]);
  }
  const { error } = await supabase.from('proposal_service_photos').delete().eq('id', photoId);
  if (error) {
    console.error('Error deleting proposal service photo:', error);
    return false;
  }
  return true;
}
